import { compact, debounce, flatten, isArray, isObject, pickBy } from "lodash"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { OTHER_OPTION_ID } from "components/forms"

const EMAIL_REGEX = /\S+@\S{2,}\.\S{2,}/

export const isEmptyish = value => {
  if (!value) return true
  if (isObject(value)) return Object.values(value).every(v => isEmptyish(v))
  if (isArray(value)) return value.every(v => isEmptyish(v))
  return false
}

export const fieldIsCustomField = ({ attributes }) =>
  attributes.field_type && attributes.field_type.match(/^custom_/)

export const fetchAddressSuggestionDebounced = debounce(
  (address, callback) => fetchAddressSuggestion(address, callback),
  600
)

const fetchAddressSuggestion = (address, callback) => {
  if (!EMAIL_REGEX.test(address)) return

  sessionApiClient
    .post("/accounts/v2/suggest_email_replacement", {
      data: {
        attributes: {
          email: address,
        },
      },
    })
    .then(({ data }) => {
      return callback(data.attributes.suggested_replacement)
    })
}

export const trackEmailSuggestionClick = (
  original_address,
  suggested_address
) => {
  sessionApiClient.post("/accounts/v2/track_email_replacement_selected_count", {
    data: {
      attributes: {
        origin: "ccw_form",
        original_address,
        suggested_address,
      },
    },
  })
}

export const triggerIsCheckboxes = (values, conditionTriggerId) =>
  typeof values[conditionTriggerId] === "object" &&
  Object.keys(values[conditionTriggerId]).includes("selected")

export const otherOptionIsChecked = conditionOptionTrigger =>
  parseInt(conditionOptionTrigger.id) === OTHER_OPTION_ID

export const optionIsSelectedInCheckboxes = ({
  conditionOptionTrigger,
  conditionTrigger,
  conditionTriggerId,
  included,
  values,
}) => {
  if (otherOptionIsChecked(conditionOptionTrigger)) {
    // it's the "other" option so check to see if "other" is selected
    return Object.keys(values[conditionTriggerId]).includes("other")
  } else {
    // it's checkboxes so check the selected array
    const fieldType = fieldIsCustomField(conditionTrigger)
      ? "FieldOption"
      : "FormFieldOption"
    return (
      values[conditionTriggerId].selected.includes(conditionOptionTrigger.id) &&
      included.find(
        f => f.id === conditionOptionTrigger.id && f.type === fieldType
      )
    )
  }
}

export const optionIsSelectedInDropdown = (
  conditionTriggerId,
  conditionOptionTrigger,
  conditionOptionValueTrigger
) =>
  conditionTriggerId === conditionOptionValueTrigger ||
  (conditionOptionTrigger && conditionTriggerId === conditionOptionTrigger.id)

export const trimmedValues = values =>
  pickBy(values, value => !(typeof value === "string" && !value.trim()))

export const valueIsCheckboxes = value =>
  value.selected && Array.isArray(value.selected)

export const valueIsAddressField = value => value.street || value.street2

export const valueIsEmptyAddress = (fieldId, value, fields) => {
  const field = fields.find(field => field.id === fieldId)
  if (field?.attributes.field_type !== "address") return false

  return !value.city && !value.street && !value.state && !value.zip
}

export const valueIsBlankDropdownOtherOption = value =>
  Object.keys(value).includes("other") && value.other.trim() === ""

export const valueIsAttachment = value => value === "attachments"

export const includedParamForCheckboxes = (value, key) => {
  let selected = value.selected.map((_k, i) => ({
    type: "FormSubmissionValue",
    attributes: {
      form_field_id: key,
      value: value.selected[i],
    },
  }))

  if (value["other"]) {
    selected = selected.concat([
      {
        type: "FormSubmissionValue",
        attributes: {
          form_field_id: key,
          value: { other: value["other"] },
        },
      },
    ])
  }
  return selected
}

export const uploadFileAttachment = async attachment => {
  const response = await Promise.all(
    attachment.files.map(file => sessionApiClient.uploadFile(file))
  )

  const files = response.map(({ data }) => ({
    fileID: data[0].id,
    fileName: data[0].attributes.name,
  }))

  return {
    form_field_id: attachment.form_field_id,
    files,
  }
}

export const buildIncludedParam = (values, uploadedAttachments, fields) =>
  flatten(
    compact(
      Object.keys(values).map(key => {
        if (valueIsCheckboxes(values[key])) {
          return includedParamForCheckboxes(values[key], key)
        } else if (valueIsAttachment(values[key])) {
          const attachment = uploadedAttachments.find(
            a => parseInt(a.form_field_id, 10) === parseInt(key, 10)
          )

          return {
            type: "FormSubmissionValue",
            attributes: {
              form_field_id: key,
              value: attachment.files,
            },
          }
        } else {
          if (valueIsAddressField(values[key])) {
            // concatenate address line 1 & 2
            values[key].street = [values[key].street, values[key].street2].join(
              "\n"
            )
            delete values[key].street2
          }

          if (valueIsEmptyAddress(key, values[key], fields)) {
            values[key] = {}
          }

          // If "other" is blank, return null which will be compacted from the final array
          if (valueIsBlankDropdownOtherOption(values[key])) return null

          return {
            type: "FormSubmissionValue",
            attributes: {
              form_field_id: key,
              value: values[key],
            },
          }
        }
      })
    )
  )

export const optionallyLimitFieldErrors = (fields, errors) => {
  for (const fieldId of Object.keys(errors)) {
    if (
      fields.find(field => field.id === fieldId).attributes.field_type ===
      "phone_number"
    ) {
      errors[fieldId] = limitFieldErrors(errors[fieldId], "phone_number")
    }
  }
}

export const limitFieldErrors = (fieldErrors, mask_error_key = null) => {
  if (Object.values(fieldErrors).length === 0) return

  let first_error = Object.entries(fieldErrors).slice(0, 1)[0]

  let [error_key, value] = [first_error[0], first_error[1]]

  let obj = {}

  if (mask_error_key === null) {
    obj[error_key] = value
  } else {
    obj[mask_error_key] = value
  }

  return obj
}

export const convertObjectValuesToString = obj =>
  Object.keys(obj).reduce((final, name) => {
    if (obj[name]) {
      final[name] = obj[name].join(", ")
    }
    return final
  }, {})

export const extractFieldErrors = formSubmissionErrors =>
  formSubmissionErrors &&
  formSubmissionErrors.meta.associated_resources.reduce((errors, e) => {
    const id = e.relationships.form_field.data.id || null
    if (id) errors[id] = convertObjectValuesToString(e.errors)
    return errors
  }, {})

export const extractAuthErrors = errors =>
  errors
    .filter(e => {
      if (!e.source) return false
      return [
        "person.first_name",
        "person.last_name",
        "person.emails.address",
        "person",
      ].includes(e.source.parameter)
    })
    .reduce((errors, error) => {
      const parameter = error.source.parameter
        .replace("person.", "")
        .replace("emails", "email")
        .replace(/\.|_/, " ")

      errors[parameter] = { [parameter]: error.detail }
      return errors
    }, {})
